import { LoaderCircle, RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext.jsx'

const TURNSTILE_LOAD_TIMEOUT_MS = 10_000
const TURNSTILE_RETRY_DELAY_MS = 600
let turnstileScriptPromise

function createLoadError(code) {
  const error = new Error(code)
  error.code = code
  return error
}

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (turnstileScriptPromise) return turnstileScriptPromise

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-turnstile-script]')
    const script = existing || document.createElement('script')
    let settled = false

    const cleanup = () => {
      window.clearTimeout(timeoutId)
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }

    const fail = (code) => {
      if (settled) return
      settled = true
      cleanup()
      turnstileScriptPromise = undefined
      script.remove()
      reject(createLoadError(code))
    }

    const handleLoad = () => {
      if (!window.turnstile) {
        fail('TURNSTILE_SCRIPT_INVALID')
        return
      }
      if (settled) return
      settled = true
      cleanup()
      resolve(window.turnstile)
    }

    const handleError = () => fail('TURNSTILE_SCRIPT_FAILED')
    const timeoutId = window.setTimeout(
      () => fail('TURNSTILE_LOAD_TIMEOUT'),
      TURNSTILE_LOAD_TIMEOUT_MS,
    )

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })

    if (!existing) {
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.dataset.turnstileScript = 'true'
      document.head.appendChild(script)
    }
  })

  return turnstileScriptPromise
}

function TurnstileWidget({ siteKey, onTokenChange }) {
  const { t } = useLanguage()
  const containerRef = useRef(null)
  const onTokenChangeRef = useRef(onTokenChange)
  const retryTimerRef = useRef()
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState('loading')
  const [errorType, setErrorType] = useState('')

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange
  }, [onTokenChange])

  useEffect(() => {
    if (!siteKey) return undefined

    let cancelled = false
    let widgetId
    let automaticRetryCount = 0
    setStatus('loading')
    setErrorType('')
    onTokenChangeRef.current('')

    const retryVerification = () => {
      onTokenChangeRef.current('')
      if (automaticRetryCount >= 1) {
        setErrorType('verification')
        setStatus('error')
        return true
      }

      automaticRetryCount += 1
      setStatus('retrying')
      window.clearTimeout(retryTimerRef.current)
      retryTimerRef.current = window.setTimeout(() => {
        if (cancelled || widgetId === undefined || !window.turnstile) return
        window.turnstile.reset(widgetId)
        setStatus('ready')
      }, TURNSTILE_RETRY_DELAY_MS)
      return true
    }

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return
        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          size: 'flexible',
          retry: 'never',
          'refresh-expired': 'never',
          callback: (token) => {
            automaticRetryCount = 0
            setStatus('verified')
            setErrorType('')
            onTokenChangeRef.current(token)
          },
          'expired-callback': retryVerification,
          'timeout-callback': retryVerification,
          'error-callback': retryVerification,
        })
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setErrorType('load')
        setStatus('error')
      })

    return () => {
      cancelled = true
      window.clearTimeout(retryTimerRef.current)
      if (widgetId !== undefined && window.turnstile) {
        window.turnstile.remove(widgetId)
      }
    }
  }, [attempt, siteKey])

  const retry = () => {
    setStatus('loading')
    setErrorType('')
    setAttempt((current) => current + 1)
  }

  return (
    <div className="turnstile-field">
      <div
        ref={containerRef}
        className="turnstile-widget"
        aria-label={t('authForm.humanCheck')}
      />
      {(status === 'loading' || status === 'retrying') && (
        <p className="turnstile-status" role="status" aria-live="polite">
          <LoaderCircle className="spin" size={16} />
          {status === 'loading' ? t('auth.turnstileLoading') : t('auth.turnstileRetrying')}
        </p>
      )}
      {status === 'verified' && (
        <p className="turnstile-status is-success" role="status">
          {t('auth.turnstileVerified')}
        </p>
      )}
      {status === 'error' && (
        <div className="turnstile-status is-error" role="alert">
          <span>
            {errorType === 'load'
              ? t('auth.turnstileLoadFailed')
              : t('auth.turnstileVerificationFailed')}
          </span>
          <button type="button" className="turnstile-retry" onClick={retry}>
            <RefreshCw size={15} />
            {t('auth.turnstileRetry')}
          </button>
        </div>
      )}
    </div>
  )
}

export default TurnstileWidget
